import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getBYOKStatus,
  getBYOKBillingHistory,
  getBYOKInvoices,
  cancelBYOKSubscription,
  subscribeBYOK
} from '../../../../apiLayer/vsCodeAuthAPIs/authAPIs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../ui/Card';
import { Button } from '../../../ui/Button';
import { Badge } from '../../../ui/Badge';
import { Skeleton } from '../../../ui/Skeleton';
import {
  CreditCard,
  FileText,
  Download,
  Calendar,
  RefreshCw,
  Key,
  Infinity,
  AlertTriangle,
  ExternalLink,
  Loader2,
  XCircle
} from 'lucide-react';
import { cn } from '../../../../lib/utils';

const BYOKBilling = ({ userInfo }) => {
  const navigate = useNavigate();
  const [byokStatus, setByokStatus] = useState(null);
  const [billingHistory, setBillingHistory] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingLoading, setBillingLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(null);

  // Fetch BYOK status
  const fetchBYOKStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getBYOKStatus();
      setByokStatus(data);
    } catch (error) {
      console.error('Error fetching BYOK status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch billing history
  const fetchBillingHistory = useCallback(async () => {
    try {
      setBillingLoading(true);
      const data = await getBYOKBillingHistory();
      setBillingHistory(data);
    } catch (error) {
      console.error('Error fetching billing history:', error);
    } finally {
      setBillingLoading(false);
    }
  }, []);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      setInvoicesLoading(true);
      const data = await getBYOKInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  // Cancel subscription
  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      const result = await cancelBYOKSubscription(cancelReason);
      if (result.success) {
        setShowCancelModal(false);
        setCancelReason('');
        await fetchBYOKStatus();
        alert(result.message || 'Subscription cancelled successfully.');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  // Subscribe to a plan
  const handleSubscribe = async (planType) => {
    try {
      setSubscribeLoading(planType);
      const result = await subscribeBYOK(planType);
      if (result.success && result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        alert(result.message || 'Failed to create checkout session.');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      alert('Failed to process subscription. Please try again.');
    } finally {
      setSubscribeLoading(null);
    }
  };

  useEffect(() => {
    fetchBYOKStatus();
    fetchBillingHistory();
    fetchInvoices();
  }, [fetchBYOKStatus, fetchBillingHistory, fetchInvoices]);

  // Format date from Unix timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'trial':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      case 'cancelled':
      case 'canceled':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      case 'expired':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/30';
    }
  };

  // Format status label
  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Get plan icon
  const getPlanIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'lifetime':
        return <Infinity className="w-5 h-5" />;
      case 'yearly':
        return <Calendar className="w-5 h-5" />;
      default:
        return <Key className="w-5 h-5" />;
    }
  };

  // Get plan details
  const getPlanDetails = (type) => {
    switch (type?.toLowerCase()) {
      case 'monthly':
        return { name: 'Monthly', price: '$10', period: '/month' };
      case 'yearly':
        return { name: 'Yearly', price: '$100', period: '/year' };
      case 'lifetime':
        return { name: 'Lifetime', price: '$160', period: ' one-time' };
      default:
        return { name: 'Free Trial', price: '$0', period: '' };
    }
  };

  const subscription = byokStatus?.subscription;
  const usage = byokStatus?.usage;
  const planDetails = getPlanDetails(subscription?.subscription_type);

  // Check if user has an active paid subscription (monthly, yearly, or lifetime)
  const hasPaidSubscription = ['monthly', 'yearly', 'lifetime'].includes(subscription?.subscription_type?.toLowerCase()) &&
    (subscription?.status === 'active' || subscription?.status === 'trial');

  // Only show trial/upgrade prompt if user does NOT have a paid subscription
  const showTrialOrUpgradePrompt = !hasPaidSubscription &&
    (subscription?.status === 'trial' || subscription?.status === 'expired' || !subscription?.subscription_type);

  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Billing</h1>
        <p className="text-sm text-muted-foreground">Manage your BYOK subscription and billing</p>
      </div>

      <div className="space-y-6 max-w-5xl">
        {/* Subscription Status */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              <CardTitle>BYOK Subscription</CardTitle>
            </div>
            <CardDescription>Your Bring Your Own Key subscription details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                </div>
              </div>
            ) : (
              <>
                {/* Main subscription info */}
                <div className="flex items-start justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-3 rounded-lg",
                      subscription?.status === 'active' ? "bg-green-500/10" :
                      subscription?.status === 'trial' ? "bg-blue-500/10" : "bg-white/5"
                    )}>
                      {getPlanIcon(subscription?.subscription_type)}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">
                          {planDetails.name} Plan
                        </h3>
                        <Badge variant="outline" className={cn(getStatusColor(subscription?.status))}>
                          {formatStatus(subscription?.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {subscription?.status === 'trial'
                          ? `${usage?.trial_tokens_remaining?.toLocaleString() || 0} trial tokens remaining`
                          : subscription?.status === 'active'
                            ? 'Full platform access with your own API keys'
                            : 'Subscribe to continue using the platform'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right">
                      <span className="text-2xl font-bold text-white">{planDetails.price}</span>
                      <span className="text-sm text-muted-foreground">{planDetails.period}</span>
                    </div>
                    {subscription?.status === 'active' && subscription?.subscription_type !== 'lifetime' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => setShowCancelModal(true)}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                </div>

                {/* Subscription details grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                      <Key className="w-4 h-4" />
                      Plan Type
                    </div>
                    <div className="text-xl font-bold text-white capitalize">
                      {subscription?.subscription_type || 'Trial'}
                    </div>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                      <RefreshCw className="w-4 h-4" />
                      Lifetime Tokens Used
                    </div>
                    <div className="text-xl font-bold text-white">
                      {usage?.lifetime_tokens_used?.toLocaleString() || 0}
                    </div>
                  </div>

                  <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                      <Calendar className="w-4 h-4" />
                      {subscription?.subscription_type === 'lifetime' ? 'Access' : 'Expires'}
                    </div>
                    <div className="text-lg font-bold text-white">
                      {subscription?.subscription_type === 'lifetime'
                        ? 'Lifetime'
                        : subscription?.expires_at
                          ? formatDate(subscription.expires_at)
                          : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Trial Warning / Upgrade Prompt */}
                {showTrialOrUpgradePrompt && (
                  <div className={cn(
                    "p-4 rounded-lg border flex items-start gap-3",
                    subscription?.status === 'expired'
                      ? "bg-red-500/10 border-red-500/20"
                      : usage?.is_trial_exhausted
                        ? "bg-yellow-500/10 border-yellow-500/20"
                        : "bg-blue-500/10 border-blue-500/20"
                  )}>
                    <AlertTriangle className={cn(
                      "w-5 h-5 mt-0.5",
                      subscription?.status === 'expired' ? "text-red-400" :
                      usage?.is_trial_exhausted ? "text-yellow-400" : "text-blue-400"
                    )} />
                    <div className="flex-1">
                      <h4 className="font-medium text-white mb-1">
                        {subscription?.status === 'expired'
                          ? 'Subscription Expired'
                          : usage?.is_trial_exhausted
                            ? 'Trial Exhausted'
                            : 'Free Trial Active'}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        {subscription?.status === 'expired'
                          ? 'Your subscription has expired. Subscribe to continue using the platform.'
                          : usage?.is_trial_exhausted
                            ? 'You have used all your trial tokens. Subscribe to continue.'
                            : `You have ${usage?.trial_tokens_remaining?.toLocaleString() || 0} tokens remaining in your trial.`}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSubscribe('monthly')}
                          disabled={subscribeLoading === 'monthly'}
                          className="bg-white/10 hover:bg-white/20"
                        >
                          {subscribeLoading === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                          Monthly $10
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSubscribe('yearly')}
                          disabled={subscribeLoading === 'yearly'}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {subscribeLoading === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                          Yearly $100
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSubscribe('lifetime')}
                          disabled={subscribeLoading === 'lifetime'}
                          variant="outline"
                        >
                          {subscribeLoading === 'lifetime' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                          Lifetime $160
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                <CardTitle>Payment History</CardTitle>
              </div>
              {billingHistory?.total_spent > 0 && (
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                  Total: ${billingHistory.total_spent.toFixed(2)}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {billingLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                    <tr>
                      <th className="px-4 py-3 rounded-l-lg">Date</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Plan</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 rounded-r-lg text-right">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory?.payments?.length > 0 ? (
                      billingHistory.payments.map((payment) => (
                        <tr key={payment.payment_id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3 text-white">{formatDate(payment.created_at)}</td>
                          <td className="px-4 py-3 text-white font-medium">${payment.amount.toFixed(2)}</td>
                          <td className="px-4 py-3 text-muted-foreground capitalize">{payment.subscription_type}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={cn(
                              payment.status === 'succeeded'
                                ? "bg-green-500/10 text-green-500 border-green-500/30"
                                : "bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
                            )}>
                              {payment.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {payment.receipt_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(payment.receipt_url, '_blank')}
                                className="h-8 hover:bg-white/10"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                          No payment history found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoices */}
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                <CardTitle>Invoices</CardTitle>
              </div>
              {invoices?.total_invoices > 0 && (
                <span className="text-sm text-muted-foreground">
                  {invoices.total_invoices} invoice{invoices.total_invoices > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-white/5">
                    <tr>
                      <th className="px-4 py-3 rounded-l-lg">Invoice #</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 rounded-r-lg text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices?.invoices?.length > 0 ? (
                      invoices.invoices.map((invoice) => {
                        const downloadUrl = invoice.invoice_pdf || invoice.pdf_url || invoice.download_url ||
                                           invoice.pdf || invoice.url || invoice.hosted_invoice_url;

                        return (
                          <tr key={invoice.invoice_id || invoice.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                            <td className="px-4 py-3 text-white font-mono text-xs">{invoice.invoice_number || invoice.number || invoice.id}</td>
                            <td className="px-4 py-3 text-white">{formatDate(invoice.created_at || invoice.created || invoice.date)}</td>
                            <td className="px-4 py-3 text-white font-medium">${(invoice.amount || invoice.total || 0).toFixed(2)}</td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className={cn(
                                invoice.status === 'paid'
                                  ? "bg-green-500/10 text-green-500 border-green-500/30"
                                  : "bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
                              )}>
                                {invoice.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                {invoice.hosted_invoice_url && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                                    className="h-8 hover:bg-white/10"
                                  >
                                    <ExternalLink className="w-4 h-4 mr-1" /> View
                                  </Button>
                                )}
                                {downloadUrl && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(downloadUrl, '_blank')}
                                    className="h-8 bg-primary/10 hover:bg-primary/20 border-primary/30"
                                  >
                                    <Download className="w-4 h-4 mr-1" /> Download
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                          No invoices found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-[#121219] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-500/10">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Cancel Subscription</h3>
                  <p className="text-sm text-muted-foreground">We're sorry to see you go</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Your subscription will remain active until the end of the current billing period.
                After that, you'll lose access to premium features.
              </p>

              <div>
                <label className="text-sm font-medium text-white mb-2 block">
                  Reason for cancellation (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please let us know why you're cancelling..."
                  className="w-full h-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                  }}
                >
                  Keep Subscription
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cancelling...</>
                  ) : (
                    'Confirm Cancellation'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BYOKBilling;
